/**
 * Strip engine machine-channel metadata from assistant reply text before display.
 *
 * Engine-side agents (e.g. contract-analyst) append HTML-comment blocks such as
 * `<!-- RISK_METADATA {...} -->` to their replies as a machine-parsable channel.
 * The conversation thread renders assistant content as plain text, so these
 * blocks would show up verbatim to the user. This helper removes:
 *
 * 1. every complete `<!-- ... -->` block, and
 * 2. a trailing unterminated `<!--` block — during streaming the metadata tail
 *    arrives token by token, and the opening half must not flash on screen
 *    before the closing `-->` lands.
 *
 * Also separates bare-URL tails from inline citation marks. When the LLM
 * appends `[2][3]` directly after a URL (e.g.
 * ``https://example.com/page42[2][3]``) the GFM autolinker treats the
 * bracketed digits as part of the URL — producing broken links like
 * ``example.com/page42%5B2``. We insert a space between the URL and the
 * first ``[n]`` so the autolinker stops at the right place; the citation
 * marks then render as plain ``[2][3]`` text alongside the link.
 */

const COMPLETE_HTML_COMMENT = /<!--[\s\S]*?-->/g;
const TRAILING_OPEN_COMMENT = /<!--[\s\S]*$/;
// Match a bare URL (no surrounding <>, no whitespace, no brackets inside)
// immediately followed by one or more ``[digit]`` citation marks. The URL
// is captured without the trailing brackets; replace glues them back with
// a separating space.
const URL_TAILING_CITATIONS = /(https?:\/\/[^\s<>\[\]]+)(\[\d+\])/g;

export function stripEngineMetadata(content: string): string {
  return content
    .replace(COMPLETE_HTML_COMMENT, "")
    .replace(TRAILING_OPEN_COMMENT, "")
    .replace(URL_TAILING_CITATIONS, "$1 $2")
    .trimEnd();
}

// ---------------------------------------------------------------------------
// READINESS_METADATA：区分"没评估"与"评估了但没读出来"（C8，迭代 35）
//
// 尽调收集顾问在回复末尾附 `<!-- READINESS_METADATA {...} -->`，右栏的齐备度清单
// 靠它渲染。此前两处解析（API 层 `dueDiligenceStream.ts` 与组件层
// `CollectionChatPanel.tsx` 各一份拷贝）都把两种失败折叠成同一个 `null`：
//
//   - **块不存在** → 本轮没做齐备度评估。这通常是合理的：不是每一轮都在评估。
//   - **块存在但 JSON 解析不出来** → 顾问评估了，而我们把结果丢了。
//
// 折叠的后果不是"少显示一点"：页面只在解析成功时才 `setLatestReadiness`，所以解析
// 失败时**清单保留上一轮的值**。律师读着新回复，右栏是旧评估，两者在界面上无法区分，
// 他可能据此派发子报告。这与迭代 13（检索空结果 vs 检索失败）、迭代 28/29（答完 vs
// 半截）是同一族缺陷：把"没拿到"说成"没有"。
//
// 因此解析结果改为三态。同时这一份放在 utils 供两侧共用——原先两份拷贝各自演进本身
// 就是漂移的起点（组件层那份注释写明"独立复制以避免反向依赖 API 层"，而 utils 层
// 不属 API 层，两侧都可以引用）。

/** READINESS_METADATA 的解析结果：三态，不把两种失败折叠成一个 null。 */
export type ReadinessParseOutcome<T> =
  /** 回复里没有该块——本轮未做齐备度评估。 */
  | { status: "absent" }
  /** 块在且解析成功。 */
  | { status: "ok"; data: T }
  /** 块在但内容读不出来——评估做了、结果丢了，必须让用户知道。 */
  | { status: "malformed"; raw: string };

const READINESS_BLOCK = /<!--\s*READINESS_METADATA\s*([\s\S]*?)-->/;

/**
 * 从助手回复中取出 READINESS_METADATA 的原始 JSON 文本，区分三种结局。
 *
 * 只负责"定位与取原文"，不做字段归一——归一各处形状不同（API 层与组件层的目标类型
 * 一致但导入路径不同），交由调用方在拿到 `raw` 后自行 `JSON.parse` 并归一。这样本
 * 函数无需依赖任何领域类型，可以安全地放在 utils 层被两侧共用。
 */
export function locateReadinessMetadata(
  reply: string,
): ReadinessParseOutcome<string> {
  const m = reply?.match(READINESS_BLOCK);
  if (!m) return { status: "absent" };
  const raw = (m[1] ?? "").trim();
  if (!raw) return { status: "malformed", raw };
  try {
    JSON.parse(raw);
  } catch {
    return { status: "malformed", raw };
  }
  return { status: "ok", data: raw };
}
